/* @flow */

import React, { Component, Fragment } from 'react';
import { WorkbenchMediaQueries as MQ } from '../../constants/WorkbenchBreakpoints';
import type { $Suggestion } from 'CoreTypes/AutoCompleteSuggestions';
import WorkbenchIcon from '../WorkbenchIcon/WorkbenchIcon';
import WorkbenchIcons from '../WorkbenchIcon/WorkbenchIcons';

type $Props = {
  mq: string,
  marketCode?: string,
  autoSuggestionEnabled: boolean,
  showCountryInSuggestions: boolean,
  locateMeLabel: string,
  noSuggestionsLabel: string,
  searchLocationString: string,
  searchLocationSuggestions: Array<$Suggestion>,
  searchLocationSuggestionsFetched: boolean,
  hoveredIndex: number,
  instantSearchSuggestions?: boolean,
  isSearchByLocationActive: boolean,
  isSearchActive: boolean,
  baseClassName: string,
  getAddressSuggestions: Function,
  submitSearch: Function,
  onLocateMeButtonClick: Function,
  onSuggestionHoverChange: Function
};

export default class DealersSearchAutocomplete extends Component<$Props> {

  topObserver: IntersectionObserver;
  bottomObserver: IntersectionObserver;
  firstChild: HTMLElement;
  lastChild: HTMLElement;

  componentDidUpdate() {
    const { isSearchByLocationActive } = this.props;

    if (this.props.autoSuggestionEnabled && ![MQ.MQ1, MQ.MQ2, MQ.MQ3].includes(this.props.mq) &&
      isSearchByLocationActive) {
      this.refreshAutosuggestionScrollObserver();
    }
  }

  toggleClassName = (toggleClassName: boolean, wrapperClassName: string, className: string) => {
    const autoSuggestionWrapper = document.getElementsByClassName(wrapperClassName)[0];
    if (autoSuggestionWrapper) {
      autoSuggestionWrapper.classList.toggle(className, toggleClassName);
    }
  }

  refreshAutosuggestionScrollObserver = () => {
    const { baseClassName } = this.props;
    const wrapperClassName = `${baseClassName}__autosuggest-suggestions-wrapper`;
    const scrollerClassName = `${baseClassName}__autosuggest-suggestions-container`

    const autoSuggestionScroller = document.getElementsByClassName(scrollerClassName)[0];

    const io_options = {
      root: autoSuggestionScroller,
      rootMargin: '5px',
      threshold: 1
    };

    //Create a top and a bottom observer, if they do not exist
    if(!this.topObserver) {
      this.topObserver = new IntersectionObserver(
        entries => this.toggleClassName(!entries[0].isIntersecting, wrapperClassName,
          `${wrapperClassName}-top-shadow`),
        io_options
      );
    }

    if(!this.bottomObserver) {
      this.bottomObserver = new IntersectionObserver(
        entries => this.toggleClassName(!entries[0].isIntersecting, wrapperClassName,
          `${wrapperClassName}-bottom-shadow`),
        io_options
      );
    }

    // This is ran after an update to the suggestions list
    // If there is already a observed child, 'unobserve' it, for it has been changed
    if (this.firstChild) this.topObserver.unobserve(this.firstChild);
    if (this.lastChild) this.bottomObserver.unobserve(this.lastChild);

    //Fetch the new top and bottom children
    const newFirstChild = autoSuggestionScroller.children[0];
    const newLastChild = autoSuggestionScroller.children[autoSuggestionScroller.children.length - 1];

    if (this.props.searchLocationSuggestions.length > 0) {
      //if there are search suggestions, observe the new first and last children
      if (newFirstChild) this.topObserver.observe(newFirstChild);
      if (newLastChild) this.bottomObserver.observe(newLastChild);
    } else {
      //If there are no suggestions, just remove both shadows
      this.toggleClassName(false, wrapperClassName,
        `${wrapperClassName}-top-shadow`);
      this.toggleClassName(false, wrapperClassName,
        `${wrapperClassName}-bottom-shadow`);
    }

    // Set the child values to the new childre, even if they are null
    this.firstChild = newFirstChild;
    this.lastChild = newLastChild;

  }

  handleAutoSuggestionOptionClick = (suggestion: $Suggestion) => {
    this.props.submitSearch(suggestion);
  }

  render() {
    const { searchLocationSuggestions, isSearchByLocationActive, isSearchActive,
      searchLocationString, baseClassName, locateMeLabel, searchLocationSuggestionsFetched } = this.props;

    const autoSuggestionWrapperClassName = `${baseClassName}__autosuggest-suggestions-wrapper`;
    const autoSuggestionScrollerClassName = `${baseClassName}__autosuggest-suggestions-container`;

    // Regex that ignores letter case and keeps the original text
    // Use it to split the text into an array and to find it in the array to apply special styles

    // DL-1768 - Added an escape character to invalid symbols for autocomplete regex
    const searchString = searchLocationString.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const searchLocationSubStringRegex = new RegExp(`(${searchString})`, 'ig');

    const locateMeIsHovered = this.props.hoveredIndex === 99;

    return (
      isSearchByLocationActive && (
        <div className={`${baseClassName}__autosuggest ${isSearchActive && ' active' || ''}`}>
          {this.props.autoSuggestionEnabled && (
            <div className={autoSuggestionWrapperClassName}>
              <div className={autoSuggestionScrollerClassName}>
                {searchLocationSuggestions.length > 0 && searchLocationSuggestions.map(
                  (suggestion, idx) => this.buildSuggestionRow(
                      searchLocationSubStringRegex, searchLocationString, baseClassName, suggestion, idx
                    )
                )}
                {searchLocationSuggestions.length === 0 && searchLocationSuggestionsFetched &&
                   <div className={`${baseClassName}__autosuggest-row-wrapper`}>
                     <div className={`${baseClassName}__autosuggest-row`}>
                     <span className={`${baseClassName}__autosuggest-row--lbl`}>
                        {this.props.noSuggestionsLabel}
                      </span>
                     </div>
                  </div>
                }
              </div>
            </div>
          )}
          <div className={`${baseClassName}__autosuggest-row ${locateMeIsHovered && 'hover' || ''}
            ${baseClassName}__geolocation`}
            onClick={this.props.onLocateMeButtonClick}
            onMouseEnter={() => this.props.onSuggestionHoverChange(99)}
            onMouseLeave={() => this.props.onSuggestionHoverChange(-1)}
          >
            <WorkbenchIcon
              icon={WorkbenchIcons.ICO_LOCATE_ME}
              modifierClass={`${baseClassName}__geolocation--ico`}
            />
            <span className={`${baseClassName}__geolocation--lbl`}>{locateMeLabel}</span>
            {locateMeIsHovered &&
              <WorkbenchIcon
                icon={WorkbenchIcons.ICO_ENTER_KEY}
                modifierClass={`${baseClassName}__geolocation--enter-ico`}
              />}
          </div>
        </div>
      )
    );
  }

  buildSuggestionRow = (
    searchLocationSubStringRegex: RegExp,
    searchLocationString: string,
    baseClassName: string,
    suggestion: $Suggestion,
    idx: number
  ) => {
    const optionStr = suggestion.label;
    const optionStrArray = optionStr.split(searchLocationSubStringRegex);
    const optionIsHovered = this.props.hoveredIndex === idx;
    return (
      <div
        id={`dlc_suggestion_${idx}`}
        key={`dlc_suggestion_${idx}`}
        className={`${baseClassName}__autosuggest-row-wrapper`}
        onMouseDown={() => this.handleAutoSuggestionOptionClick(suggestion)}
        onMouseEnter={() => this.props.onSuggestionHoverChange(idx)}
      >
        <div className={`${baseClassName}__autosuggest-row ${optionIsHovered && 'hover' || ''}`} >
          <span className={`${baseClassName}__autosuggest-row--lbl`} >
            {optionStrArray.map((item, i) => (
              <Fragment key={`${baseClassName}__autosuggest-row--lbl-${i}`}>
                {searchLocationString !== "" && item.match(searchLocationSubStringRegex) ?
                  <strong className={`${baseClassName}__autosuggest-row--lbl--highlight`}>
                    {item}
                  </strong> :
                  item
                }
              </Fragment>
            ))}
          </span>
          {optionIsHovered &&
            <WorkbenchIcon
              icon={WorkbenchIcons.ICO_ENTER_KEY}
              modifierClass={`${baseClassName}__autosuggest-row--ico`}
            />}
        </div>
      </div>
    );
  }
}
